/**
 * 管理端统一 HTTP Transport（fetch 封装）。
 *
 * 职责：基址、Authorization、超时、`{success,data,timestamp}` 信封解包、错误归一化（保留 `error.code`）、
 * 401 清空会话+重定向登录、W3C `traceparent` 注入、`apiRequest` span（路径模板化，ID 不拼入 span 名）。
 * 对齐 `frontend-api-endpoints.md`、`frontend-rum.md` §3/§5、`frontend-api-testing.md` §2。
 *
 * 解耦：token 获取与 401 处理通过 `configureTransport` 注入，避免 transport 直接依赖 React 上下文。
 */
import { context, propagation, trace, type Span } from "@opentelemetry/api";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api";
const TIMEOUT_MS = 15000;

/** 注入点：token 获取（由 SessionContext 注入） */
let tokenProvider: () => string | null = () => null;
/** 注入点：401 处理（清空会话 + 重定向登录，由 SessionContext 注入） */
let unauthorizedHandler: () => void = () => undefined;

/**
 * 身份上下文（注入到请求 header，供后端 RequestContextMiddleware 解析 RequestContext）。
 * - organizationId/callerId/callerKind/campusScope 四头齐全，后端据此注入 RequestContext（机构级主数据查询锚定）；
 * - 口径与家长端 guardian-token.middleware 一致（x-organization-id/x-caller-kind/x-caller-id）；
 * - 2-2 staff-rbac 落地后由真实鉴权中间件保证；一期由 SessionContext 从登录 session 镜像读取
 *   （staff 登录时 callerKind=staff、callerId=staffId、campusScope 由校区授权决定）。
 */
export interface RequestIdentity {
  organizationId?: string;
  callerId?: string;
  callerKind?: string;
  campusScope?: string;
}
/** 注入点：身份上下文（由 SessionContext 注入，读 session 镜像） */
let identityProvider: () => RequestIdentity | null = () => null;

/** 配置 Transport 注入点（应用启动时调用一次）。 */
export function configureTransport(opts: {
  getToken: () => string | null;
  getIdentity?: () => RequestIdentity | null;
  onUnauthorized: () => void;
}): void {
  tokenProvider = opts.getToken;
  identityProvider = opts.getIdentity ?? (() => null);
  unauthorizedHandler = opts.onUnauthorized;
}

/** 业务/网络错误基类，保留稳定 `code`，调用方按 code 映射，不依赖中文 message 分支。 */
export class ApiError extends Error {
  /** 服务端 error.details 结构化业务明细（可选；如排课硬冲突 conflicts 数组）。 */
  readonly details?: unknown;

  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
    this.details = details;
  }
}

interface SuccessEnvelope<T> {
  success: true;
  data: T;
  timestamp: string;
}
interface FailureEnvelope {
  success: false;
  error: { code: string; message: string; details?: unknown };
  timestamp: string;
}
type Envelope<T> = SuccessEnvelope<T> | FailureEnvelope;

/** 路径模板化：将路径中的 UUID/数字 ID 替换为 `{id}`，防 span 名高基数。 */
function templatePath(path: string): string {
  return path
    .replace(
      /\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g,
      "/{id}",
    )
    .replace(/\/\d+/g, "/{id}");
}

/** 懒取 Tracer，避免模块加载时缓存到尚未注册的 noop Provider。 */
function getTransportTracer() {
  return trace.getTracer("admin-web-transport");
}

/**
 * 构造带鉴权与追踪的请求头（request 与 download 共用）。
 *
 * 身份 header 注入（供后端 RequestContextMiddleware 解析 RequestContext）：
 * organizationId/callerId/callerKind/campusScope 四头齐全。2-2 真实鉴权后由中间件保证，
 * 一期由 SessionContext 从登录 session 镜像注入（staff: callerKind=staff, callerId=staffId）。
 */
function buildAuthHeaders(init?: HeadersInit): Headers {
  const headers = new Headers(init);
  // W3C traceparent 注入：经 OTel propagation 写入 headers，后端入口据此续链
  propagation.inject(context.active(), headers, {
    set: (carrier: Headers, key: string, value: string) =>
      carrier.set(key, value),
  });
  const token = tokenProvider();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const identity = identityProvider();
  if (identity?.organizationId)
    headers.set("x-organization-id", identity.organizationId);
  if (identity?.callerId) headers.set("x-caller-id", identity.callerId);
  headers.set("x-caller-kind", identity?.callerKind ?? "staff");
  headers.set("x-campus-scope", identity?.campusScope ?? "all");
  return headers;
}

/**
 * 凭据类 401 判定：登录/改密接口的「账号密码错误」，非会话失效。
 * 此类 401 不清会话不跳登录（用户尚在登录页），透传服务端业务文案（如「账号或密码错误」）。
 */
const CREDENTIAL_ERROR_CODES = new Set([
  "identity.login_failed",
  "identity.password_expired",
]);
function isCredentialError(code: string): boolean {
  return CREDENTIAL_ERROR_CODES.has(code);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const method = init.method ?? "GET";
  const spanName = `apiRequest: ${method} ${templatePath(path)}`;

  return getTransportTracer().startActiveSpan(spanName, async (span: Span) => {
    span.setAttribute("http.method", method);
    span.setAttribute("http.url", url);

    const headers = buildAuthHeaders(init.headers);
    headers.set("Content-Type", "application/json");

    try {
      // 超时用 Promise.race（jsdom undici 对 AbortSignal 兼容性不稳定，改用 race 避免传 signal）
      const fetchPromise = fetch(url, { ...init, headers });
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(
          () =>
            reject(new ApiError("NETWORK", "请求超时了，请检查网络后重试", 0)),
          TIMEOUT_MS,
        ),
      );
      const res = (await Promise.race([
        fetchPromise,
        timeoutPromise,
      ])) as Response;
      span.setAttribute("http.status_code", res.status);

      // 先解包信封再分支：401 需读 error.code 区分语义（凭据错误 vs 会话过期）
      const body = (await res.json().catch(() => null)) as Envelope<T> | null;

      if (res.status === 401) {
        // 凭据类 401（登录接口的账号密码错误）：透传服务端文案，不清会话不跳登录
        if (body?.success === false && isCredentialError(body.error.code)) {
          throw new ApiError(body.error.code, body.error.message, 401);
        }
        // 会话类 401（令牌无效/过期）：清空会话 + 重定向登录页
        unauthorizedHandler();
        throw new ApiError(
          body?.success === false ? body.error.code : "UNAUTHORIZED",
          body?.success === false
            ? body.error.message
            : "登录已过期，请重新登录",
          401,
        );
      }

      if (!body || typeof body.success !== "boolean") {
        throw new ApiError(
          "SHAPE_DRIFT",
          "服务返回了无法识别的内容，请稍后重试",
          res.status,
        );
      }
      if (!body.success) {
        // 403 统一中文文案：页面无需各自 catch 映射（真实原因经 code 与服务端日志排查）
        throw new ApiError(
          body.error.code,
          res.status === 403 ? "无权限执行此操作" : body.error.message,
          res.status,
          body.error.details,
        );
      }
      return body.data;
    } catch (e) {
      if (e instanceof ApiError) throw e;
      span.recordException(e as Error);
      // 网络失败/超时/断网统一归一化为 NETWORK 错误
      throw new ApiError("NETWORK", "网络连接异常，请检查网络后重试", 0);
    } finally {
      span.end();
    }
  });
}

/** 文件下载结果：响应体与从 Content-Disposition 解析出的文件名（可能缺失）。 */
export interface DownloadedFile {
  blob: Blob;
  filename: string | null;
}

/** 下载超时独立于 JSON 请求（大文件导出可达分钟级，15s 不够）。 */
const DOWNLOAD_TIMEOUT_MS = 120_000;

/**
 * 文件下载请求（CSV 等二进制附件）。
 *
 * 与 `request` 的差异：成功响应不走 JSON 信封解包而返回 Blob；
 * 失败响应（服务端恒以 JSON 信封返回错误）仍按信封解包抛 `ApiError`，401 语义与 `request` 一致。
 */
async function download(
  path: string,
  params?: Record<string, unknown>,
): Promise<DownloadedFile> {
  const qs = params
    ? `?${new URLSearchParams(params as Record<string, string>).toString()}`
    : "";
  const url = `${BASE_URL}${path}${qs}`;
  const spanName = `apiDownload: GET ${templatePath(path)}`;

  return getTransportTracer().startActiveSpan(spanName, async (span: Span) => {
    span.setAttribute("http.method", "GET");
    span.setAttribute("http.url", url);

    const headers = buildAuthHeaders();

    try {
      // 超时用 Promise.race（jsdom undici 对 AbortSignal 兼容性不稳定，改用 race 避免传 signal）
      const fetchPromise = fetch(url, { headers });
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(
          () =>
            reject(new ApiError("NETWORK", "请求超时了，请检查网络后重试", 0)),
          DOWNLOAD_TIMEOUT_MS,
        ),
      );
      const res = (await Promise.race([
        fetchPromise,
        timeoutPromise,
      ])) as Response;
      span.setAttribute("http.status_code", res.status);

      const contentType = res.headers.get("content-type") ?? "";
      // 失败路径：服务端以 JSON 信封返回错误（含 401 会话失效）
      if (!res.ok || contentType.includes("application/json")) {
        const body = (await res.json().catch(() => null)) as
          | Envelope<unknown>
          | null;

        if (res.status === 401) {
          unauthorizedHandler();
          throw new ApiError(
            body?.success === false ? body.error.code : "UNAUTHORIZED",
            body?.success === false
              ? body.error.message
              : "登录已过期，请重新登录",
            401,
          );
        }
        if (body?.success === false) {
          // 403 统一中文文案（与 request 口径一致）
          throw new ApiError(
            body.error.code,
            res.status === 403 ? "无权限执行此操作" : body.error.message,
            res.status,
            body.error.details,
          );
        }
        throw new ApiError(
          "SHAPE_DRIFT",
          "服务返回了无法识别的内容，请稍后重试",
          res.status,
        );
      }

      // 成功路径：取附件文件名并返回 Blob
      const disposition = res.headers.get("content-disposition") ?? "";
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/);
      return {
        blob: await res.blob(),
        filename: filenameMatch?.[1] ?? null,
      };
    } catch (e) {
      if (e instanceof ApiError) throw e;
      span.recordException(e as Error);
      throw new ApiError("NETWORK", "网络连接异常，请检查网络后重试", 0);
    } finally {
      span.end();
    }
  });
}

/** 统一 HTTP 调用对象，业务 `*.api.ts` 经此发起请求。 */
export const http = {
  get: <T>(path: string, params?: Record<string, unknown>): Promise<T> => {
    const qs = params
      ? `?${new URLSearchParams(params as Record<string, string>).toString()}`
      : "";
    // no-store：权限快照等敏感 GET 禁用浏览器 HTTP 缓存，防角色切换后读到旧缓存串号
    return request<T>(`${path}${qs}`, { cache: "no-store" });
  },
  download,
  post: <T>(
    path: string,
    body?: unknown,
    headers?: Record<string, string>,
  ): Promise<T> =>
    request<T>(path, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
      headers,
    }),
  patch: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, {
      method: "PATCH",
      body: body ? JSON.stringify(body) : undefined,
    }),
  put: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, {
      method: "PUT",
      body: body ? JSON.stringify(body) : undefined,
    }),
  delete: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, {
      method: "DELETE",
      body: body ? JSON.stringify(body) : undefined,
    }),
};
